import { useState } from 'react';
import { Settings, Copy, Check, AlertTriangle, ExternalLink } from 'lucide-react';
import type { VoiceFormData } from '../VoiceChannelSettings';
import type { ExotelSetup } from '@/services/voice.service';
import { sanitizePhoneInput } from '@/lib/phone';

interface ExotelProviderFormProps {
  formData: VoiceFormData;
  onChange: (field: keyof VoiceFormData, value: unknown) => void;
  setup: ExotelSetup | null;
  isLoadingSetup: boolean;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200';

const SETUP_STEPS = [
  'In the Exotel console, open App Bazaar and create a new call flow.',
  'Add a Voicebot applet. It must be Voicebot, not Stream — Stream is one-way and cannot play our audio back to the caller.',
  'Paste the URL above into the applet.',
  'Assign the flow to your ExoPhone.',
  'Ask Exotel support to enable bidirectional streaming on your account, if they have not already.',
];

/**
 * Exotel provider configuration.
 *
 * Deliberately shorter than the Twilio tab, and not for lack of finishing:
 * taking a call never calls Exotel's API, Exotel calls us. What Exotel needs
 * for that is the applet URL below, pasted into their console, which is why
 * the centre of this tab is a copy button rather than a credential form.
 *
 * The one thing that does call Exotel's API is fetching a call's recording
 * afterwards, so the API details are asked for under "Call recordings" and
 * are optional: calls work without them, there is just nothing to play back.
 */
export default function ExotelProviderForm({
  formData,
  onChange,
  setup,
  isLoadingSetup,
}: ExotelProviderFormProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!setup?.appletUrl) return;
    try {
      await navigator.clipboard.writeText(setup.appletUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied; the field is selectable either way.
      setCopied(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Exotel Settings
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>ExoPhone Number *</label>
            <input
              type="text"
              value={formData.phoneNumber}
              onChange={(e) => onChange('phoneNumber', sanitizePhoneInput(e.target.value))}
              inputMode="tel"
              placeholder="02241491134"
              className={inputClass}
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              The Exotel number customers will call.
            </p>
          </div>
        </div>
      </div>

      {/* Call recordings: the only part of the integration that calls Exotel's API */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white">Call recordings</h4>
        <p className="text-xs text-gray-500 mt-1 mb-3 dark:text-gray-400">
          Optional. Exotel records each call; with these details the recording is fetched after
          the call so it can be played here. Find them in the Exotel dashboard under API
          Credentials. Recording itself is switched on in your Exotel call flow.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="exotel-account-sid" className={labelClass}>Account SID</label>
            <input
              id="exotel-account-sid"
              type="text"
              autoComplete="off"
              value={formData.exotelAccountSid}
              onChange={(e) => onChange('exotelAccountSid', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="exotel-subdomain" className={labelClass}>API subdomain</label>
            <input
              id="exotel-subdomain"
              type="text"
              autoComplete="off"
              value={formData.exotelSubdomain}
              onChange={(e) => onChange('exotelSubdomain', e.target.value)}
              placeholder="api.exotel.com"
              className={inputClass}
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              api.exotel.com (Singapore) or api.in.exotel.com (Mumbai), as your API Credentials page shows.
            </p>
          </div>
          <div>
            <label htmlFor="exotel-api-key" className={labelClass}>API key</label>
            <input
              id="exotel-api-key"
              type="text"
              autoComplete="off"
              value={formData.exotelApiKey}
              onChange={(e) => onChange('exotelApiKey', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="exotel-api-token" className={labelClass}>API token</label>
            <input
              id="exotel-api-token"
              type="password"
              autoComplete="new-password"
              value={formData.exotelApiToken}
              onChange={(e) => onChange('exotelApiToken', e.target.value)}
              placeholder={formData.exotelApiTokenSet ? 'Saved — leave blank to keep it' : ''}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Language question */}
      <div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.languageMenuEnabled}
            onChange={(e) => onChange('languageMenuEnabled', e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className={labelClass}>Ask the caller which language they prefer</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              After the greeting the caller is asked, in English, which language they would
              like to continue in. They can name any language the system recognises — say
              Kannada and the rest of the call is in Kannada — and their answer is used for
              the whole call. Leave this off and the language is detected automatically from
              whatever they say.
            </span>
          </span>
        </label>
      </div>

      {/* Applet URL — the one thing Exotel actually needs from us */}
      <div>
        <label className={labelClass}>Voicebot Applet URL</label>
        {isLoadingSetup ? (
          <div className="h-10 rounded-md bg-gray-100 animate-pulse dark:bg-gray-800" />
        ) : setup?.appletUrl ? (
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={setup.appletUrl}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Voicebot applet URL"
              className={`${inputClass} font-mono text-xs bg-gray-50 dark:bg-gray-800`}
            />
            <button
              type="button"
              onClick={handleCopy}
              className="shrink-0 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700 flex items-center gap-1.5"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-green-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              The applet URL cannot be shown because the server has no public webhook host
              configured. Ask your administrator to set <code>VOICE_WEBHOOK_BASE_URL</code>.
            </span>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
          This URL is generated for your organization. Paste it into the Exotel Voicebot applet.
        </p>
      </div>

      {setup && !setup.streamConfigured && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            The voice media stream is not configured on the server yet, so Exotel calls will not
            connect. Ask your administrator to set the Exotel stream endpoint and token.
          </span>
        </div>
      )}

      {/* Setup guidance */}
      <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800/60">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
          Setting up the Exotel side
          <a
            href="https://docs.exotel.com/exotel-agentstream/voicebot-applet"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-1 text-xs font-normal dark:text-blue-400"
          >
            Exotel docs
            <ExternalLink className="h-3 w-3" />
          </a>
        </h4>
        <ol className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-300">
          {SETUP_STEPS.map((step, index) => (
            <li key={step} className="flex gap-3 leading-relaxed">
              <span className="shrink-0 w-5 h-5 rounded-full bg-gray-200 text-gray-700 text-xs flex items-center justify-center font-medium dark:bg-gray-700 dark:text-gray-200">
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>

      <p className="text-sm text-gray-500 leading-relaxed dark:text-gray-400">
        Speech recognition and synthesis for Exotel calls are configured on the server, not here.
        Your greeting, language and handoff settings below apply to Exotel exactly as they do to
        Twilio.
      </p>
    </div>
  );
}
